/**
 * Format a YYYY-MM-DD close date for display (e.g. "Apr 23, 2026").
 *
 * Shared by DealCardContent (store-fed) and the proposal card builders
 * (payload-fed) so a deal's close date reads the same on every surface.
 * Anything that isn't a valid calendar date returns undefined so the caller
 * can fall back rather than render a raw string.
 */
export function formatCloseDateString(
  raw: string | undefined
): string | undefined {
  if (!raw) {
    return undefined;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) {
    return undefined;
  }
  const [, yStr, mStr, dStr] = match;
  const year = Number(yStr);
  const monthIdx = Number(mStr) - 1;
  const day = Number(dStr);
  // Use UTC and round-trip through getUTC* to reject impossible dates
  // (e.g. 2026-02-31) that Date() would otherwise silently normalize
  // forward by a few days. Suppress impossible dates entirely instead
  // of rendering the raw string.
  const date = new Date(Date.UTC(year, monthIdx, day));
  if (
    isNaN(date.getTime()) ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== monthIdx ||
    date.getUTCDate() !== day
  ) {
    return undefined;
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
